import {
  Component,
  computed,
  Input,
  input,
  Output,
  EventEmitter,
  signal,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

export interface SidebarPopoutItem {
  id: string;
  label: string;
  labelIcon?: string;
  labelIconTooltip?: string;
  showAction?: boolean;
  actionIcon?: string;
  actionTooltip?: string;
}

export interface SidebarPopoutGroup {
  key: string;
  items: SidebarPopoutItem[];
}

@Component({
  selector: 'app-sidebar-popout',
  imports: [MatIcon, MatTooltip],
  templateUrl: './sidebar-popout.html',
  styleUrl: './sidebar-popout.scss',
})
export class SidebarPopout {
  @Input({ required: true }) title = '';
  @Input({ required: true }) icon = '';
  readonly groups = input<SidebarPopoutGroup[]>([]);
  @Input() activeItemId: string | null = null;
  @Input() searchPlaceholder = '';
  @Input() emptyMessage = '';
  @Input() showAdd = false;
  @Input() addLabel = '';
  @Input() showSettings = false;
  @Input() settingsLabel = '';

  @Input() sortToggleIcon = 'filter_list';
  @Input() sortToggleTitle = '';

  @Output() readonly popoutClose = new EventEmitter<void>();
  @Output() readonly itemSelect = new EventEmitter<string>();
  @Output() readonly itemAction = new EventEmitter<string>();
  @Output() readonly add = new EventEmitter<void>();
  @Output() readonly settings = new EventEmitter<void>();
  @Output() readonly sortToggle = new EventEmitter<void>();

  protected readonly searchQuery = signal('');
  protected readonly collapsedGroups = signal<Set<string>>(new Set());

  protected readonly filteredGroups = computed<SidebarPopoutGroup[]>(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return this.groups();

    return this.groups()
      .map((g) => ({
        ...g,
        items: g.items.filter((item) =>
          item.label.toLowerCase().includes(query),
        ),
      }))
      .filter((g) => g.items.length > 0);
  });

  protected onSearch(value: string): void {
    this.searchQuery.set(value);
  }

  protected isGroupExpanded(key: string): boolean {
    return !this.collapsedGroups().has(key);
  }

  protected toggleGroup(key: string): void {
    this.collapsedGroups.update((set) => {
      const next = new Set(set);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }
}
